// Per Ankh Enhanced Side Navigation
// Auth-aware: shows Sign In for guests, Dashboard + Sign Out for members

import {
  STATIC_LINKS,
  GUEST_LINKS,
  MEMBER_LINKS,
  ADMIN_LINKS,
  MEMBER_ONLY_LINKS,
} from '../SiteHeader/nav-links.js';
import { getClerkSession, onAuthChange } from '../../auth/clerk-session.js';
import { canOpenLabs, canEnterRealm } from '../../auth/member-roles.js';
import { mountTabBar } from '../TabBar/TabBar.js';
import { startLiveUpdates } from '../../../shared/live.js';
import { getPushState, enablePush, disablePush, addManifestLink, loadPrefs, savePrefs } from '../../../shared/push.js';
import '../TabBar/TabBar.css';

(function () {
  let isAnimating = false;
  let clerkInstance = null;
  let listEl = null;
  let footerEl = null;
  /** The phone tab bar (TabBar.js), re-rendered whenever the drawer's links are. */
  let tabBar = null;

  // ── Helpers ────────────────────────────────────────────────────────────────
  function el(tag, cls, html) {
    const e = document.createElement(tag);
    if (cls)  e.className = cls;
    if (html) e.innerHTML = html;
    return e;
  }

  // el()'s third argument is innerHTML, which is right for the drawer's own icons and
  // labels. It is wrong for anything a member wrote — a name, a post, a message — so
  // every one of those goes through here and lands as text.
  function textEl(tag, cls, text) {
    const e = el(tag, cls);
    e.textContent = text == null ? '' : String(text);
    return e;
  }

  function isActive(href) {
    try {
      const p = window.location.pathname;
      return href === '/' ? (p === '/' || p === '/pages/index.html') : (p === href || p.startsWith(href + '/'));
    } catch { return false; }
  }

function linkEl(l, idx) {
  const wrap = el('div', 'pa-link-wrapper');
  const a = el('a', 'pa-side-link' + (isActive(l.href) ? ' active' : '') + (l.cls ? ' ' + l.cls : ''));
  a.href = l.href;
  a.style.animationDelay = `${idx * 0.08}s`;
  a.style.cssText += 'pointer-events:auto;cursor:pointer;display:block;text-decoration:none;';

  const row = el('div');
  row.style.cssText = 'display:flex;align-items:flex-start;gap:16px;width:100%;';

  const icon = el('span', 'pa-link-icon', l.icon);
  const txt = el('div');
  txt.style.cssText = 'display:flex;flex-direction:column;flex:1;';
  txt.appendChild(el('span', 'pa-link-label', l.label));
  txt.appendChild(el('span', 'pa-link-desc', l.description));

  if (l.badge) {
    const badge = el('span', 'pa-inbox-badge', '');
    badge.id = 'inbox-badge';
    badge.style.cssText = 'display:none;min-width:20px;height:20px;border-radius:10px;background:var(--pa-gold,#DAA520);color:#000;font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;padding:0 6px;margin-left:auto;line-height:1;';
    txt.appendChild(badge);
  }

  row.appendChild(icon);
  row.appendChild(txt);
  a.appendChild(row);

    a.addEventListener('click', e => { e.preventDefault(); window.location.href = l.href; });
    a.addEventListener('mouseenter', () => { if (!isAnimating) a.classList.add('hover'); });
    a.addEventListener('mouseleave', () => a.classList.remove('hover'));

    wrap.appendChild(a);
    return wrap;
  }

  function signOutBtn() {
    const wrap = el('div', 'pa-link-wrapper');
    const btn = el('button', 'pa-side-link pa-side-link--signout');
    btn.style.cssText = 'width:100%;background:none;border:none;text-align:left;cursor:pointer;pointer-events:auto;';

    const row = el('div');
    row.style.cssText = 'display:flex;align-items:flex-start;gap:16px;width:100%;';

    const icon = el('span', 'pa-link-icon', '🚪');
    const txt = el('div');
    txt.style.cssText = 'display:flex;flex-direction:column;flex:1;';
    txt.appendChild(el('span', 'pa-link-label', 'Sign Out'));
    txt.appendChild(el('span', 'pa-link-desc', 'Sign out of your account'));

    row.appendChild(icon);
    row.appendChild(txt);
    btn.appendChild(row);

    btn.addEventListener('click', async () => {
      if (clerkInstance) {
        await clerkInstance.signOut();
        window.location.href = '/';
      }
    });

    wrap.appendChild(btn);
    return wrap;
  }

  function renderLinks(user) {
    if (!listEl) return;
    listEl.innerHTML = '';
    tabBar?.update(user, { canEnterRealm: canEnterRealm(user) });

    const isSignedIn = !!user;
    const isAdmin = user?.publicMetadata?.role === 'admin';
    // The members' rooms stay invisible to anyone who cannot reach them: signed in
    // is not enough. An approved member sees the Realm (which comes with
    // membership) and the two study rooms, where the door offers the Study Pass.
    const roomLinks = MEMBER_ONLY_LINKS.filter(() => canEnterRealm(user) || canOpenLabs(user));

    // Sections with a heading each, rather than one list of fifteen links: on a
    // phone this drawer IS the navigation, and a heading is what lets a thumb find
    // its part of the site without reading every row.
    const sections = [
      { title: isSignedIn ? 'Your space' : 'Join us', links: isSignedIn ? MEMBER_LINKS : GUEST_LINKS },
      { title: 'Labs', links: roomLinks },
      { title: 'Explore', links: STATIC_LINKS },
      { title: 'Admin', links: isAdmin ? ADMIN_LINKS : [] },
    ];

    let i = 0;
    for (const section of sections) {
      if (!section.links.length) continue;
      listEl.appendChild(textEl('div', 'pa-side-section-title', section.title));
      for (const l of section.links) listEl.appendChild(linkEl(l, i++));
    }
    if (isSignedIn) listEl.appendChild(signOutBtn());

if (footerEl && isSignedIn) {
    const name = user.firstName || user.fullName?.split(' ')[0] || 'Member';
    footerEl.innerHTML = `
<div class="pa-footer-text">Signed in as</div>
<div class="pa-footer-sub" style="color:var(--pa-gold);font-weight:600;">${name}</div>`;
  } else if (footerEl) {
    footerEl.innerHTML = `
<div class="pa-footer-text">© ${new Date().getFullYear()} Per Ankh</div>
<div class="pa-footer-sub">Entheogenic Church</div>`;
  }
}

async function updateInboxBadge() {
  if (!clerkInstance?.user) return;
  try {
    const res = await fetch('/api/member/conversations/unread-count', { credentials: 'include' });
    if (res.ok) {
      const data = await res.json();
      // The phone tab bar shows the same count.
      window.dispatchEvent(new CustomEvent('pa:unread', { detail: { count: Number(data.unreadCount) || 0 } }));
      const badge = document.getElementById('inbox-badge');
      if (badge) {
        if (data.unreadCount > 0) {
          badge.textContent = data.unreadCount > 99 ? '99+' : data.unreadCount;
          badge.style.display = 'flex';
        } else {
          badge.style.display = 'none';
        }
      }
    }
  } catch (_) {}
}

  // ── Notifications bell ─────────────────────────────────────────────────────
  // The bell lives here rather than in a component of its own because the drawer is
  // the only piece of chrome every member page loads, and it already holds the
  // signed-in session and the 30-second refresh the Inbox badge uses. A second
  // component would have meant initialising Clerk twice on the same page.
  //
  // Everything a member wrote — a name, a post body, a message — is written with
  // textContent. Building these rows with innerHTML would let a post become markup.
  let notifyBell = null;
  let notifyPanel = null;
  let notifyList = null;
  let notifyCount = null;
  let notifyData = null;

  function timeAgo(iso) {
    if (!iso) return '';
    // SQLite writes 'YYYY-MM-DD HH:MM:SS' in UTC; make that explicit for the browser.
    const t = Date.parse(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
    if (Number.isNaN(t)) return '';
    const secs = Math.max(0, Math.floor((Date.now() - t) / 1000));
    if (secs < 60) return 'just now';
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  function snippet(text, max = 90) {
    const s = String(text || '').replace(/\s+/g, ' ').trim();
    return s.length > max ? s.slice(0, max - 1) + '…' : s;
  }

  // A post written inside a Circle is not shown in the main feed, so a notification
  // about it has to send the member to the Circles page or it would look empty.
  function itemUrl(item) {
    if (item.type === 'message') return `/member/inbox?c=${encodeURIComponent(item.id)}`;
    if (item.type === 'circle') return `/member/circles/${encodeURIComponent(item.circleSlug)}`;
    return item.circleId
      ? '/member/circles'
      : `/member/feed?post=${encodeURIComponent(item.postId)}`;
  }

  function itemTitle(item) {
    if (item.type === 'message') return `${item.actor.name} sent you a message`;
    if (item.type === 'circle') {
      const names = item.actorNames || [];
      if (item.total === 1) return `${names[0] || 'Someone'} posted in ${item.circleName}`;
      return `${item.total} new posts in ${item.circleName}`;
    }
    // A welcome post was written by the church, but its replies are for the member.
    const yourPost = item.isWelcome ? 'your welcome post' : 'your post';
    if (item.type === 'comment' && item.isReplyToYou) return `${item.actor.name} replied to your comment`;
    if (item.type === 'comment') return `${item.actor.name} commented on ${yourPost}`;
    const names = item.actorNames || [];
    if (names.length === 1) return `${names[0]} reacted to ${yourPost}`;
    if (names.length === 2) return `${names[0]} and ${names[1]} reacted to ${yourPost}`;
    if (names.length > 2) return `${names[0]}, ${names[1]} and ${names.length - 2} more reacted to ${yourPost}`;
    return `Someone reacted to ${yourPost}`;
  }

  const KIND_LABEL = { message: 'Message', comment: 'Comment', reaction: 'Reaction', circle: 'Circle' };

  function buildBell() {
    if (notifyBell) return;

    notifyBell = el('button', 'pa-notify-bell');
    notifyBell.type = 'button';
    notifyBell.setAttribute('aria-label', 'Notifications');
    notifyBell.setAttribute('aria-expanded', 'false');
    notifyBell.style.display = 'none';

    notifyCount = el('span', 'pa-notify-count', '');
    notifyCount.style.display = 'none';
    notifyBell.appendChild(el('span', 'pa-notify-bell-icon', '🔔'));
    notifyBell.appendChild(notifyCount);

    notifyPanel = el('div', 'pa-notify-panel');
    notifyPanel.hidden = true;
    notifyPanel.setAttribute('role', 'dialog');
    notifyPanel.setAttribute('aria-label', 'Notifications');

    const head = el('div', 'pa-notify-head');
    head.appendChild(el('span', 'pa-notify-title', 'Notifications'));
    const closeBtn = el('button', 'pa-notify-close', '✕');
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Close notifications');
    closeBtn.addEventListener('click', closeNotifications);
    head.appendChild(closeBtn);

    notifyList = el('div', 'pa-notify-list');

    notifyPanel.appendChild(head);
    notifyPanel.appendChild(notifyList);
    notifyPanel.appendChild(buildPushRow());

    notifyBell.addEventListener('click', (e) => {
      e.preventDefault();
      if (notifyPanel.hidden) openNotifications(); else closeNotifications();
    });

    document.addEventListener('click', (e) => {
      if (notifyPanel.hidden) return;
      if (notifyPanel.contains(e.target) || notifyBell.contains(e.target)) return;
      closeNotifications();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !notifyPanel.hidden) closeNotifications();
    });

    document.body.appendChild(notifyBell);
    document.body.appendChild(notifyPanel);
  }

  // ── Phone notifications switch ─────────────────────────────────────────────
  // Sits at the foot of the bell panel. Nothing is asked of the browser until the
  // member taps it. (iPhones need the site on the Home Screen first; see shared/push.js.)
  let pushRow = null, pushText = null, pushBtn = null;
  async function authHeaders() {
    try {
      const token = await clerkInstance?.session?.getToken();
      return token ? { Authorization: `Bearer ${token}` } : {};
    } catch (_) { return {}; }
  }
  function buildPushRow() {
    pushRow = el('div', 'pa-push-row');
    pushRow.hidden = true;
    pushText = el('span', 'pa-push-text', '');
    pushBtn = el('button', 'pa-push-btn', '');
    pushBtn.type = 'button';
    pushBtn.addEventListener('click', async () => {
      const turningOn = pushBtn.dataset.mode === 'on';
      pushBtn.disabled = true;
      try {
        if (turningOn) await enablePush(authHeaders); else await disablePush(authHeaders);
      } catch (err) {
        pushText.textContent = err.message || 'Something went wrong.';
        pushBtn.disabled = false;
        return;
      }
      pushBtn.disabled = false;
      refreshPushRow();
    });
    pushRow.appendChild(pushText);
    pushRow.appendChild(pushBtn);
    pushRow.appendChild(buildPushSettings());
    return pushRow;
  }

  // What to buzz about: shown once phone notifications are on for this device.
  const HOURS = Array.from({ length: 24 }, (_, h) => h);
  const hourLabel = (h) => `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? 'am' : 'pm'}`;
  let pushSettings = null, pushSettingsBtn = null, prefInputs = {};
  function buildPushSettings() {
    pushSettingsBtn = el('button', 'pa-push-settings-btn', 'Choose what to be notified about');
    pushSettingsBtn.type = 'button';
    pushSettingsBtn.setAttribute('aria-expanded', 'false');
    pushSettingsBtn.hidden = true;
    pushSettings = el('div', 'pa-push-settings');
    pushSettings.hidden = true;

    const row = (key, label, hint) => {
      const wrap = el('label', 'pa-pref');
      const box = document.createElement('input');
      box.type = 'checkbox';
      box.addEventListener('change', () => persistPrefs({ [key]: box.checked }));
      prefInputs[key] = box;
      const words = el('span', 'pa-pref-words');
      words.appendChild(el('span', 'pa-pref-label', label));
      if (hint) words.appendChild(el('span', 'pa-pref-hint', hint));
      wrap.appendChild(box);
      wrap.appendChild(words);
      return wrap;
    };
    pushSettings.appendChild(row('messages', 'New messages'));
    pushSettings.appendChild(row('comments', 'Comments and replies'));
    pushSettings.appendChild(row('preview', 'Show who and what in the notification', 'Off keeps your lock screen private: it only says you have something new.'));
    pushSettings.appendChild(row('quietEnabled', 'Quiet hours', 'No phone buzzes in this time. You still see everything in the app.'));

    const quiet = el('div', 'pa-pref-quiet');
    for (const [key, label] of [['quietStart', 'From'], ['quietEnd', 'Until']]) {
      const wrap = el('label', 'pa-pref-hour', label + ' ');
      const sel = document.createElement('select');
      for (const h of HOURS) { const o = document.createElement('option'); o.value = String(h); o.textContent = hourLabel(h); sel.appendChild(o); }
      sel.addEventListener('change', () => persistPrefs({ [key]: Number(sel.value) }));
      prefInputs[key] = sel;
      wrap.appendChild(sel);
      quiet.appendChild(wrap);
    }
    pushSettings.appendChild(quiet);
    pushSettings.appendChild(el('div', 'pa-pref-status', ''));

    pushSettingsBtn.addEventListener('click', async () => {
      const open = pushSettings.hidden;
      pushSettings.hidden = !open;
      pushSettingsBtn.setAttribute('aria-expanded', String(open));
      if (open) await fillPrefs();
    });
    const frag = document.createDocumentFragment();
    frag.appendChild(pushSettingsBtn);
    frag.appendChild(pushSettings);
    return frag;
  }
  function setPrefStatus(text) {
    const s = pushSettings?.querySelector('.pa-pref-status');
    if (s) s.textContent = text;
  }
  async function fillPrefs() {
    try {
      const p = await loadPrefs(authHeaders);
      for (const k of ['messages', 'comments', 'preview', 'quietEnabled']) prefInputs[k].checked = !!p[k];
      prefInputs.quietStart.value = String(p.quietStart);
      prefInputs.quietEnd.value = String(p.quietEnd);
      setPrefStatus('');
    } catch (err) { setPrefStatus(err.message); }
  }
  async function persistPrefs(patch) {
    try { await savePrefs(authHeaders, patch); setPrefStatus('Saved'); }
    catch (err) { setPrefStatus(err.message); }
  }
  async function refreshPushRow() {
    if (!pushRow) return;
    const state = await getPushState(authHeaders);
    pushRow.hidden = state === 'unsupported' || state === 'unavailable';
    pushBtn.hidden = state !== 'off' && state !== 'on';
    if (pushSettingsBtn) {
      pushSettingsBtn.hidden = state !== 'on';
      if (state !== 'on') { pushSettings.hidden = true; pushSettingsBtn.setAttribute('aria-expanded', 'false'); }
    }
    if (state === 'off') { pushText.textContent = 'Get a buzz on this device for new messages and replies.'; pushBtn.textContent = 'Turn on'; pushBtn.dataset.mode = 'on'; }
    else if (state === 'on') { pushText.textContent = 'Phone notifications are on for this device.'; pushBtn.textContent = 'Turn off'; pushBtn.dataset.mode = 'off'; }
    else if (state === 'denied') pushText.textContent = 'Notifications are blocked for this site. Allow them in your browser settings to turn them on.';
    else if (state === 'ios-install') pushText.textContent = 'To get phone notifications on iPhone: tap Share, then “Add to Home Screen”, and open Per Ankh from there.';
  }

  function renderNotificationList(data) {
    if (!notifyList) return;
    notifyList.innerHTML = '';

    if (!data.items.length) {
      const empty = el('div', 'pa-notify-empty');
      empty.appendChild(el('div', 'pa-notify-empty-title', 'Nothing new right now'));
      empty.appendChild(el('div', 'pa-notify-empty-sub', 'Messages, replies to your posts and new posts in your Circles show up here.'));
      notifyList.appendChild(empty);
      return;
    }

    data.items.forEach((item) => {
      const row = el('a', 'pa-notify-item');
      row.href = itemUrl(item);

      const avatar = el('span', 'pa-notify-avatar');
      if (item.actor && item.actor.avatarPath) {
        const img = document.createElement('img');
        img.src = `/api/member/avatars/${encodeURIComponent(item.actor.avatarPath)}`;
        img.alt = '';
        avatar.appendChild(img);
      } else {
        avatar.textContent = item.type === 'message' ? '✉️' : item.type === 'reaction' ? '💛' : item.type === 'circle' ? '⭕' : '💬';
      }

      const body = el('span', 'pa-notify-body');
      body.appendChild(textEl('span', 'pa-notify-line', itemTitle(item)));
      body.appendChild(textEl('span', 'pa-notify-preview', snippet(item.type === 'reaction' ? item.postText : item.text)));
      body.appendChild(textEl('span', 'pa-notify-time', timeAgo(item.at)));

      row.appendChild(avatar);
      row.appendChild(body);
      row.appendChild(textEl('span', 'pa-notify-kind', KIND_LABEL[item.type] || ''));
      notifyList.appendChild(row);
    });
  }

  function updateBell(data) {
    if (!notifyBell) return;
    notifyBell.style.display = 'flex';
    if (data.total > 0) {
      notifyCount.textContent = data.total > 99 ? '99+' : String(data.total);
      notifyCount.style.display = 'flex';
      notifyBell.classList.add('has-new');
      notifyBell.setAttribute('aria-label', `Notifications (${data.total} new)`);
    } else {
      notifyCount.style.display = 'none';
      notifyBell.classList.remove('has-new');
      notifyBell.setAttribute('aria-label', 'Notifications');
    }
  }

  // `keepList` is for the refresh that follows marking the list seen: the badge should
  // drop, but pulling the entries out from under the member who is reading them would
  // look like the notification deleted itself.
  async function loadNotifications({ keepList = false } = {}) {
    if (!clerkInstance?.user) return;
    try {
      const res = await fetch('/api/member/notifications', { credentials: 'include' });
      if (!res.ok) return;
      notifyData = await res.json();
      buildBell();
      updateBell(notifyData);
      if (!keepList && notifyPanel && !notifyPanel.hidden) renderNotificationList(notifyData);
    } catch (_) {}
  }

  async function openNotifications() {
    if (!notifyPanel) return;
    if (notifyData) renderNotificationList(notifyData);
    notifyPanel.hidden = false;
    refreshPushRow();
    notifyBell.setAttribute('aria-expanded', 'true');
    notifyBell.classList.add('open');

    // Opening the list is what "seen" means for comments and reactions. Messages are
    // left alone on purpose: they keep counting until they are actually read in the
    // Inbox, so the number can legitimately stay up after the list has been opened.
    if (notifyData && (notifyData.newComments > 0 || notifyData.newReactions > 0)) {
      try {
        await fetch('/api/member/notifications/seen', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
      } catch (_) {}
      await loadNotifications({ keepList: true });
    }
  }

  function closeNotifications() {
    if (!notifyPanel) return;
    notifyPanel.hidden = true;
    notifyBell.setAttribute('aria-expanded', 'false');
    notifyBell.classList.remove('open');
  }

  function hideBell() {
    if (notifyBell) notifyBell.style.display = 'none';
    closeNotifications();
  }

  // ── Build DOM ──────────────────────────────────────────────────────────────
  function buildNav() {
    const toggle = el('button', 'pa-nav-toggle', '<span class="hamburger"></span><span class="pa-menu-label">MENU</span>');
    toggle.setAttribute('aria-label', 'Open navigation menu');

    toggle.addEventListener('mousemove', e => {
      if (!toggle.classList.contains('active')) {
        const r = toggle.getBoundingClientRect();
        toggle.style.transform = `translate(${Math.max(-5,Math.min(5,(e.clientX-r.left-r.width/2)*0.1))}px,${Math.max(-5,Math.min(5,(e.clientY-r.top-r.height/2)*0.1))}px) scale(1.05)`;
      }
    });
    toggle.addEventListener('mouseleave', () => { if (!toggle.classList.contains('active')) toggle.style.transform = ''; });

    const overlay = el('div', 'pa-side-overlay');
    overlay.setAttribute('aria-hidden', 'true');

    const panel = el('aside', 'pa-side-nav');
    panel.setAttribute('role', 'navigation');
    panel.setAttribute('aria-label', 'Side navigation');

    // Header / logo
    const header = el('div', 'pa-side-header');
    const logoCon = el('div', 'pa-logo-container');
    const logo = el('img', 'pa-logo');
    logo.src = '/assets/images/logo/PerAnkhLogo.png';
    logo.alt = 'Per Ankh Entheogenic Church Logo';
    logo.style.cssText = 'height:60px;width:auto;';
    logoCon.appendChild(logo);
    header.appendChild(logoCon);

    listEl = el('div', 'pa-side-links');
    footerEl = el('div', 'pa-side-footer');

    panel.appendChild(header);
    panel.appendChild(listEl);
    panel.appendChild(footerEl);

    // Render guest links immediately (fast paint before Clerk resolves)
    renderLinks(null);

    // ── Open / Close ───────────────────────────────────────────────────────
    const open = () => {
      if (isAnimating) return;
      isAnimating = true;
      panel.classList.add('open');
      overlay.classList.add('open');
      toggle.classList.add('active');
      toggle.setAttribute('aria-label', 'Close navigation');
      panel.querySelectorAll('.pa-side-link').forEach(l => l.classList.add('animate-in'));
      setTimeout(() => { isAnimating = false; }, 300);
    };

    const close = () => {
      if (isAnimating) return;
      isAnimating = true;
      panel.classList.remove('open');
      overlay.classList.remove('open');
      toggle.classList.remove('active');
      toggle.setAttribute('aria-label', 'Open navigation');
      panel.querySelectorAll('.pa-side-link').forEach(l => l.classList.remove('animate-in'));
      setTimeout(() => { isAnimating = false; }, 300);
    };

    // Expose open/close so the persistent SiteHeader top bar can drive the
    // same mobile drawer (single source of truth for navigation).
    window.perAnkhSideNav = { open, close };

    toggle.addEventListener('click', e => {
      e.preventDefault();

      // Ripple
      const ripple = document.createElement('span');
      const r = toggle.getBoundingClientRect(), sz = Math.max(r.width, r.height);
      ripple.style.cssText = `width:${sz}px;height:${sz}px;left:${e.clientX-r.left-sz/2}px;top:${e.clientY-r.top-sz/2}px;`;
      ripple.classList.add('ripple');
      toggle.appendChild(ripple);
      setTimeout(() => ripple.parentNode?.removeChild(ripple), 600);

      toggle.classList.add('bounce');
      setTimeout(() => toggle.classList.remove('bounce'), 200);

      if ('vibrate' in navigator) navigator.vibrate(50);

      // Drum sound
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator(), gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(60, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(20, ctx.currentTime + 0.1);
        gain.gain.setValueAtTime(0.25, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
        osc.connect(gain); gain.connect(ctx.destination);
        osc.start(); osc.stop(ctx.currentTime + 0.2);
      } catch (_) {}

      panel.classList.contains('open') ? close() : open();
    });

    overlay.addEventListener('click', close);
    document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });

    // Swipe gestures
    let startX = 0, curX = 0;
    panel.addEventListener('touchstart', e => { startX = e.touches[0].clientX; });
    panel.addEventListener('touchmove',  e => { curX = e.touches[0].clientX; });
    panel.addEventListener('touchend',   () => { if (startX - curX > 50) close(); });

    document.body.appendChild(toggle);
    document.body.appendChild(overlay);
    document.body.appendChild(panel);
    tabBar = mountTabBar();

    initClerkAuth();
  }

  // ── Async Clerk init ───────────────────────────────────────────────────────
  // One shared session for the whole page (header, drawer, assistant). Member-only
  // destinations are gated on the approved-member role, not merely on sign-in.
  async function initClerkAuth() {
    let session = null;
    try {
      session = await getClerkSession();
    } catch (err) {
      console.warn('[SideNav] Clerk init failed:', err?.message || err);
    }

    if (!session) {
      console.warn('[SideNav] No Clerk session — guest nav only');
      return;                                  // guest links are already rendered
    }

    clerkInstance = session;
    addManifestLink();
    renderLinks(session.user || null);
    updateInboxBadge();
    loadNotifications();
    startLive();
    // A slow safety check, in case a live answer is ever lost on the way. The live loop
    // below is what makes the badge and the bell change the moment something happens.
    setInterval(() => { updateInboxBadge(); loadNotifications(); }, 120000);

    onAuthChange(session, (user) => {
      renderLinks(user);
      if (user) {
        updateInboxBadge();
        loadNotifications();
        startLive();
      } else {
        hideBell();
        stopLive();
      }
    });
  }

  // ── Live updates ───────────────────────────────────────────────────────────
  // One loop per page, started once someone is signed in. When the server says
  // something changed, the badge and the bell re-read at once, and `pa:live` tells
  // the rest of the page (the Inbox listens for it) to refresh what it shows.
  let live = null;
  function startLive() {
    if (live || !clerkInstance?.user) return;
    live = startLiveUpdates({
      headers: async () => {
        try {
          const token = await clerkInstance.session?.getToken();
          return token ? { Authorization: `Bearer ${token}` } : {};
        } catch (_) { return {}; }
      },
      onChange: () => {
        updateInboxBadge();
        loadNotifications();
        window.dispatchEvent(new CustomEvent('pa:live'));
      },
    });
  }
  function stopLive() {
    live?.stop();
    live = null;
  }

  // ── Boot ───────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', buildNav);
  } else {
    buildNav();
  }
})();
